/**
 * ChatSidebar Component
 * Sidebar principale con lista chat e ricerca
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { RefreshCw, SlidersHorizontal, Clock, SquarePen, Archive, ArrowLeft, Megaphone } from 'lucide-react';
import { ChatSearchBar } from './ChatSearchBar';
import { ChatList } from './ChatList';
import { ChatFiltersBar } from './ChatFiltersBar';
import { NewChatModal, type NewChatTarget } from './NewChatModal';
import { SearchSuggestions } from './SearchSuggestions';
import { ScheduledMessagesPanel } from './ScheduledMessagesPanel';
import { useWhatsAppChats } from '@/lib/hooks/whatsapp';
import { useWhatsAppChatStore } from '@/lib/stores/whatsappChatStore';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/lib/supabase';
import { toggleDebugPanel, debugLog } from '../chat/ChatDebugPanel';
import { SectionDiscoveryBanner } from '@/components/discovery/SectionDiscoveryBanner';
import { tip } from '@/components/messages/ChatTooltip';
import { useGroupRealtime } from '../group/useGroupRealtime';
import { isCloudService } from '@/lib/services/whatsapp/newChatLogic';
import { useRecontactLoader } from '../chat/useChatRecontact';
import { BulkSendModal } from '../bulk/BulkSendModal';

export function ChatSidebar() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showScheduled, setShowScheduled] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [newChat, setNewChat] = useState<{ query?: string; target?: NewChatTarget | null } | null>(null);
  const [showBulk, setShowBulk] = useState(false);

  const {
    chats: allChats,
    filteredChats,
    loading,
    error,
    loadingMore,
    hasMore,
    loadMore,
    refresh,
    isRefreshing,
    isSearching,
    archivedCount,
    clientsWithoutChat,
    moreOnServer,
    loadAll,
    loadingAll,
  } = useWhatsAppChats(searchQuery, { showArchived });

  // P2: Selettori individuali per evitare re-render inutili
  const selectedChatId = useWhatsAppChatStore(s => s.selectedChatId);
  const selectChat = useWhatsAppChatStore(s => s.selectChat);
  const addChatToStore = useWhatsAppChatStore(s => s.addChatToStore);
  const presenceMap = useWhatsAppChatStore(s => s.presenceMap);
  const wahaConfig = useWhatsAppChatStore(s => s.wahaConfig);
  // Nomi dei gruppi aggiornati dal vivo anche senza una chat aperta (broadcast del core).
  useGroupRealtime(wahaConfig?.organizationId, undefined, !isCloudService(wahaConfig?.serviceName));
  // Promemoria "Ricontatta" (indicatore in lista, filtro, striscia nella chat).
  useRecontactLoader(wahaConfig?.organizationId);
  const setChatUnreadCount = useWhatsAppChatStore(s => s.setChatUnreadCount);
  const sendReadReceipt = useWhatsAppChatStore(s => s.sendReadReceipt);
  const markChatAsUnreadWaha = useWhatsAppChatStore(s => s.markChatAsUnread);
  const drafts = useWhatsAppChatStore(s => s.drafts);

  const filters = useWhatsAppChatMetaStore(s => s.filters);
  const fetchChatMeta = useWhatsAppChatMetaStore(s => s.fetchChatMeta);
  const fetchChatLabels = useWhatsAppChatMetaStore(s => s.fetchChatLabels);
  const fetchOrgLabels = useWhatsAppChatMetaStore(s => s.fetchOrgLabels);
  const chatLabels = useWhatsAppChatMetaStore(s => s.chatLabels);

  const hasActiveFilters = !!(filters.status || filters.labelId || filters.myChatsOnly || filters.myProjectsOnly || filters.unreadOnly || filters.groupsOnly || filters.recontactOnly);
  const searching = !!searchQuery.trim();
  // Se si esce dalle archiviate svuotate, si torna alla lista normale.
  useEffect(() => {
    if (showArchived && archivedCount === 0 && !searching) setShowArchived(false);
  }, [showArchived, archivedCount, searching]);

  // Carica org labels al montaggio
  useEffect(() => {
    if (wahaConfig?.organizationId) {
      fetchOrgLabels(wahaConfig.organizationId);
    }
  }, [wahaConfig?.organizationId, fetchOrgLabels]);

  // Carica meta + labels per tutte le chat caricate
  useEffect(() => {
    if (!wahaConfig?.organizationId || allChats.length === 0) return;
    const jids = allChats.map((c) => c.id);
    fetchChatMeta(jids, wahaConfig.organizationId);
    fetchChatLabels(jids, wahaConfig.organizationId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allChats.length, wahaConfig?.organizationId]);

  // Polling unread DB rimosso da qui — vive in WhatsAppDrawer (sempre montato),
  // così il badge menu globale resta aggiornato anche col drawer chiuso.

  // P1: Polling ogni 15s (era 5s = 12 call/min → ora 4 call/min) con guard anti-concorrenza
  const isRefreshingRef = useRef(false);
  const safeRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    try { await refresh(); } finally { isRefreshingRef.current = false; }
  }, [refresh]);

  // P1: Polling ogni 15s (era 5s = 12 call/min → ora 4 call/min)
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (interval) return;
      interval = setInterval(() => {
        safeRefresh();
      }, 15000);
    };

    const stopPolling = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        // Delay refresh per dare tempo al token auth di refresharsi dopo tab switch
        setTimeout(() => {
          safeRefresh();
        }, 500);
        startPolling();
      }
    };

    if (!document.hidden) {
      startPolling();
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [safeRefresh]);

  // Log filteredChats per debug
  useEffect(() => {
    debugLog('Sidebar', `filteredChats: ${filteredChats.length}/${allChats.length} (filtri: ${hasActiveFilters ? 'attivi' : 'nessuno'}, search: "${searchQuery || '-'}")`);
  }, [filteredChats.length, allChats.length, hasActiveFilters, searchQuery]);

  const handleSelectChat = useCallback((chatId: string) => {
    const chatFromList = filteredChats.find(c => c.id === chatId);
    if (chatFromList) {
      addChatToStore(chatFromList);
    }
    selectChat(chatId);
  }, [selectChat, filteredChats, addChatToStore]);

  // Context menu: marca come letta (sendReadReceipt fa già locale + WAHA + DB)
  const handleMarkAsRead = useCallback((chatId: string) => {
    sendReadReceipt(chatId);
  }, [sendReadReceipt]);

  // Context menu: marca come non letta (ottimistico + WAHA + DB)
  const handleMarkAsUnread = useCallback((chatId: string) => {
    setChatUnreadCount(chatId, 1);
    markChatAsUnreadWaha(chatId);
    // Persisti nel DB (fire-and-forget)
    if (user?.id && wahaConfig?.configId && wahaConfig?.organizationId) {
      supabase.rpc('mark_whatsapp_chat_as_unread', {
        p_user_id: user.id,
        p_config_id: wahaConfig.configId,
        p_organization_id: wahaConfig.organizationId,
        p_chat_jid: chatId,
      }).then(({ error }) => {
        if (error) console.error('[ReadStatus] DB mark_as_unread error:', error);
      });
    }
  }, [setChatUnreadCount, markChatAsUnreadWaha, user?.id, wahaConfig?.configId, wahaConfig?.organizationId]);

  return (
    <div className="flex flex-col h-full bg-white dark:bg-gray-900 border-r border-[rgba(55,53,47,0.09)] dark:border-white/10">
      {/* Discovery banner */}
      {!loading && allChats.length !== undefined && (
        <div className="flex-shrink-0 px-3 pt-3">
          <SectionDiscoveryBanner section="whatsapp" count={allChats.length} />
        </div>
      )}

      {/* Header */}
      <div className="flex-shrink-0 p-4 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-[#37352F] dark:text-white">
            Chat
          </h2>
          <div className="flex items-center gap-1">
            {/* Nuova chat */}
            <button
              onClick={() => setNewChat({})}
              className="p-1.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors"
              {...tip('Nuova chat: cerca un Lead o Cliente, oppure scrivi un numero')}
              aria-label="Nuova chat"
            >
              <SquarePen className="w-4 h-4" />
            </button>

            {/* Scrivi a piu' persone */}
            <button
              onClick={() => setShowBulk(true)}
              className="p-1.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#787774] hover:text-[#37352F] dark:hover:text-white transition-colors"
              {...tip("Scrivi a piu' persone: Lead di una fase o chat con un'etichetta, con resoconto degli invii")}
              aria-label="Scrivi a piu' persone"
            >
              <Megaphone className="w-4 h-4" />
            </button>

            {/* Filtri */}
            <button
              onClick={() => { setShowFilters(v => !v); setShowScheduled(false); }}
              className={`p-1.5 rounded transition-colors ${showFilters || hasActiveFilters ? 'bg-[#2383E2]/10 text-[#2383E2]' : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#787774]'}`}
              {...tip(showFilters ? 'Chiudi i filtri' : 'Filtra le chat (non lette, etichette, assegnatario)')}
              aria-label="Filtri"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {hasActiveFilters && !showFilters && (
                <span className="sr-only">Filtri attivi</span>
              )}
            </button>

            {/* Programmati */}
            <button
              onClick={() => { setShowScheduled(v => !v); setShowFilters(false); }}
              className={`p-1.5 rounded transition-colors ${showScheduled ? 'bg-[#2383E2]/10 text-[#2383E2]' : 'hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 text-[#787774]'}`}
              {...tip('Messaggi programmati')}
              aria-label="Messaggi programmati"
            >
              <Clock className="w-4 h-4" />
            </button>

            {/* Debug panel toggle (solo dev) */}
            {import.meta.env.DEV && (
              <button
                onClick={toggleDebugPanel}
                className="px-1.5 py-0.5 rounded text-[9px] font-mono font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors border border-emerald-200 dark:border-emerald-800"
                {...tip('Debug Panel')}
              >
                DBG
              </button>
            )}

            <button
              onClick={refresh}
              disabled={isRefreshing}
              className="p-1.5 rounded hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors disabled:opacity-50"
              {...tip('Aggiorna la lista delle chat')}
              aria-label="Aggiorna"
            >
              <RefreshCw className={`w-4 h-4 text-[#787774] ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
        <ChatSearchBar value={searchQuery} onChange={setSearchQuery} loading={isSearching} placeholder="Cerca chat, Lead, Cliente o numero..." />
      </div>

      {/* Filters bar */}
      {showFilters && (
        <div className="flex-shrink-0 pt-2 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <ChatFiltersBar
            hasActiveFilters={hasActiveFilters}
            partialData={moreOnServer}
            loadedCount={allChats.length}
            onLoadAll={loadAll}
            loadingAll={loadingAll}
          />
        </div>
      )}

      {/* Archiviate: ingresso alla sezione / ritorno alla lista */}
      {!showScheduled && !searching && (showArchived || archivedCount > 0) && (
        <button
          type="button"
          onClick={() => setShowArchived((v) => !v)}
          className="flex-shrink-0 mx-2 mt-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[#37352F] dark:text-white hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5"
        >
          {showArchived ? <ArrowLeft className="w-4 h-4 text-[#787774]" /> : <Archive className="w-4 h-4 text-[#787774]" />}
          <span className="flex-1 text-left">{showArchived ? 'Torna a tutte le chat' : 'Archiviate'}</span>
          {!showArchived && <span className="text-xs text-[#787774]">{archivedCount}</span>}
        </button>
      )}

      {/* Ricerca: Lead/Clienti senza chat */}
      {!showScheduled && searching && clientsWithoutChat.length > 0 && (
        <SearchSuggestions
          clients={clientsWithoutChat}
          onPick={(target) => setNewChat({ target })}
        />
      )}

      {/* Scheduled messages panel */}
      {showScheduled && wahaConfig?.organizationId ? (
        <ScheduledMessagesPanel
          organizationId={wahaConfig.organizationId}
          onClose={() => setShowScheduled(false)}
        />
      ) : (
        /* Chat List con infinite scroll */
        <ChatList
          chats={filteredChats}
          selectedChatId={selectedChatId}
          presenceMap={presenceMap}
          chatLabels={chatLabels}
          drafts={drafts}
          onSelectChat={handleSelectChat}
          onMarkAsRead={handleMarkAsRead}
          onMarkAsUnread={handleMarkAsUnread}
          loading={loading}
          error={error}
          hasMore={hasMore}
          loadingMore={loadingMore}
          onLoadMore={loadMore}
          onRetry={refresh}
          organizationId={wahaConfig?.organizationId}
          emptyMessage={
            searching ? (isSearching ? 'Cerco in tutte le chat...' : 'Nessuna chat trovata')
              : showArchived ? 'Nessuna chat archiviata'
              : hasActiveFilters ? (moreOnServer ? 'Nessuna chat con questi filtri fra quelle caricate' : 'Nessuna chat con questi filtri')
              : 'Nessuna chat ancora'
          }
          emptyContent={searching && !isSearching ? (
            <SearchSuggestions query={searchQuery} clients={[]} onPick={(target) => setNewChat({ target })} />
          ) : undefined}
        />
      )}

      {showBulk && <BulkSendModal onClose={() => setShowBulk(false)} />}

      {newChat && (
        <NewChatModal
          initialQuery={newChat.query}
          initialTarget={newChat.target ?? null}
          onClose={() => setNewChat(null)}
        />
      )}
    </div>
  );
}
